import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./api";

// Types for Posts API
export interface Post {
  doc_id: string;
  text: string;
  timestamp: string;
  created_at: string;
  message_type: "post" | "media_post" | "media_evaluation";
  media_urls?: string[];
  post_id?: string;
  metrics?: {
    likes: number;
    retweets: number;
    replies: number;
    views: number;
  };
  metadata?: {
    evaluation_results?: Record<string, any>;
    user_profile?: any;
    context?: string[];
    [key: string]: any;
  };
}

interface PostsResponse {
  data: Post[];
  pagination?: {
    cursors: Record<string, string>;
    has_more: boolean;
  };
}

// API function to fetch posts
export async function getPosts(
  persona_id: string,
  limit: number = 50,
  message_type?: string,
  nextCursors?: Record<string, string>,
  sortOrder: "asc" | "desc" = "desc",
): Promise<PostsResponse> {
  try {
    const params: Record<string, any> = {
      persona_id,
      limit,
      sort_order: sortOrder,
    };

    // Add message_type filter if provided
    if (message_type) {
      params.message_type = message_type;
    }

    // Add next_cursors if provided
    if (nextCursors && Object.keys(nextCursors).length > 0) {
      params.next_cursors = JSON.stringify(nextCursors);
    }

    const response = await apiClient.get(`/api/posts`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching posts:", error);
    throw error;
  }
}

// React Query hook for fetching posts
export function usePostsQuery(
  personaId: string,
  limit: number = 50,
  messageType?: string,
  sortOrder: "asc" | "desc" = "desc",
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["posts", personaId, messageType, sortOrder],
    queryFn: async () => {
      return await getPosts(
        personaId,
        limit,
        messageType,
        undefined,
        sortOrder,
      );
    },
    enabled: !!personaId && enabled,
    refetchInterval: 60000, // Refetch every minute
  });
}
